import { ClaimStatus, type PolicyBundle } from '../../utils/types/Entity';

type FeaturedBundle = {
  bundle?: PolicyBundle;
  isCurrent: boolean;
};

export type HomeAuthedSummary = {
  coveredTripsCount: number;
  activeClaimsCount: number;
  openClaimsCount: number;
  featuredBundle?: PolicyBundle;
  isCurrent: boolean;
};

export const getBundleLocation = (bundle: PolicyBundle): string =>
  bundle.policies[0]?.exposures[0]?.name || 'Your protected trip';

const getFeaturedBundle = (
  policyBundles: PolicyBundle[],
  now: Date,
): FeaturedBundle => {
  const sortedBundles = [...policyBundles].sort(
    (first, second) =>
      new Date(first.coverageStartDate).getTime() -
      new Date(second.coverageStartDate).getTime(),
  );
  const currentBundle = sortedBundles.find(
    (bundle) =>
      new Date(bundle.coverageStartDate) <= now &&
      new Date(bundle.coverageEndDate) >= now,
  );

  if (currentBundle) return { bundle: currentBundle, isCurrent: true };

  return {
    bundle: sortedBundles.find(
      (bundle) => new Date(bundle.coverageStartDate) > now,
    ),
    isCurrent: false,
  };
};

export const getHomeAuthedSummary = (
  policyBundles: PolicyBundle[],
  now: Date,
): HomeAuthedSummary => {
  const availableBundles = policyBundles.filter((bundle) => !bundle.canceledAt);
  const coveredTripsCount = availableBundles.filter(
    (bundle) => new Date(bundle.coverageEndDate) >= now,
  ).length;
  const activeClaims = availableBundles.flatMap((bundle) =>
    bundle.policies.flatMap((policy) =>
      policy.exposures.flatMap((exposure) =>
        exposure.claims.filter(
          (claim) =>
            claim.status === ClaimStatus.Open ||
            claim.status === ClaimStatus.Processing,
        ),
      ),
    ),
  );
  const featuredBundle = getFeaturedBundle(availableBundles, now);

  return {
    coveredTripsCount,
    activeClaimsCount: activeClaims.length,
    openClaimsCount: activeClaims.filter(
      (claim) => claim.status === ClaimStatus.Open,
    ).length,
    featuredBundle: featuredBundle.bundle,
    isCurrent: featuredBundle.isCurrent,
  };
};
